import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@context/AuthContext';
import Main from '@pages/Main';
import Landing from '@pages/Landing';
import { PaymentSuccess } from '@pages/PaymentSuccess';
import { ProfilePage } from '@pages/ProfilePage';
import PatentLandingPage from '@pages/PatentLandingPage';
import PatentFullTextDisplay from '@pages/PatentFullTextDisplay';
import DraftsList from '@pages/DraftsList';
import DraftEditor from '@pages/DraftEditor';
import { ResolutionWarningModal } from '@/components/ResolutionWarningModal';

// Protected Route component
function ProtectedRoute({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
}

// Landing Route component (redirect to app if already logged in)
function LandingRoute() {
  const { user, loading, setUser, checkAuth } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleLoginSuccess = (token: string, username: string) => {
    console.log('Login successful, updating auth state:', username);
    // Token and username are already stored in localStorage by the API service
    // Update the auth context state to trigger navigation
    setUser({ username });
    // Alternatively, we can call checkAuth which will read from localStorage
    // checkAuth();
  };

  return <Landing onLoginSuccess={handleLoginSuccess} />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LandingRoute />} />
      <Route path="/success" element={<PaymentSuccess />} />
      <Route path="/patent" element={<PatentLandingPage />} />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/drafts"
        element={
          <ProtectedRoute>
            <DraftsList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/drafts/:draftId"
        element={
          <ProtectedRoute>
            <DraftEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/:docNumber"
        element={<PatentFullTextDisplay />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Main />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ResolutionWarningModal />
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
